import { LocationDescriptorObject, LocationDescriptor } from "history";
import { useMemo } from "react";
import { useContract, useRole } from "../ContractContext";
import { ContractPath } from "./ContractPath";
import { ContractUrls, useContractUrls } from "./ContractUrls";
import { Role } from "../model/ContractViewModel";
import { useLocation } from "react-router-dom";

function usePagesForRole() {
  const role = useRole();
  const pages = useContractFormRoutes();
  const pagesForRole = useMemo(
    () => pages.filter((x) => x.only === undefined || x.only === role),
    [role, pages]
  );
  return pagesForRole;
}

export enum NavigationDirection {
  Back,
  Forward,
}

function addNavigationDirection(
  location: LocationDescriptorObject,
  direction: NavigationDirection
) {
  const prevState = typeof location.state === "object" ? location.state : {};

  return {
    ...location,
    state: {
      ...prevState,
      direction,
    },
  };
}

function getRedirectWithDirection(
  pagesForRole: ContractFormRoute[],
  current: ContractPath,
  direction: NavigationDirection = NavigationDirection.Forward
) {
  const nextPage = getNextPage(pagesForRole, current);
  const prevPage = getPrevPage(pagesForRole, current);
  const targetPage =
    direction === NavigationDirection.Back ? prevPage : nextPage;

  return { direction, targetPage };
}

export interface NavigationDirectionState {
  direction?: NavigationDirection;
}

export interface RelativeUrls {
  next: LocationDescriptor | null;
  prev: LocationDescriptor | null;
  redirect: LocationDescriptor | null;
}

export function useRelativeUrls(current: ContractPath): RelativeUrls {
  const urls = useContractUrls();
  const pagesForRole = usePagesForRole();
  const location = useLocation<NavigationDirectionState | undefined>();
  const nextPage = getNextPage(pagesForRole, current);
  const prevPage = getPrevPage(pagesForRole, current);
  const redirect = useMemo(
    () =>
      getRedirectWithDirection(
        pagesForRole,
        current,
        location.state?.direction
      ),
    [pagesForRole, current, location]
  );
  const result = useMemo(
    () => ({
      next: nextPage
        ? addNavigationDirection(
            urls[nextPage.path],
            NavigationDirection.Forward
          )
        : null,
      prev: prevPage
        ? addNavigationDirection(urls[prevPage.path], NavigationDirection.Back)
        : null,
      redirect: redirect.targetPage
        ? addNavigationDirection(
            urls[redirect.targetPage.path],
            redirect.direction
          )
        : null,
    }),
    [urls, prevPage, nextPage, redirect]
  );
  return result;
}

export function useRedirectUrlWithDirectionFromLocationState(
  current: ContractPath
) {
  const urls = useContractUrls();
  const pagesForRole = usePagesForRole();
  const location = useLocation<NavigationDirectionState | undefined>();
  const { direction, targetPage } = getRedirectWithDirection(
    pagesForRole,
    current,
    location.state?.direction
  );
  const targetUrl = targetPage ? urls[targetPage.path] : null;
  const result = useMemo(
    () => (targetUrl ? addNavigationDirection(targetUrl, direction) : null),
    [targetUrl, direction]
  );
  return result;
}

function getNextPage(pagesForRole: ContractFormRoute[], current: ContractPath) {
  const currentIndex = pagesForRole.findIndex(
    (x) => x.path === current || x.also?.find((y) => y === current)
  );
  if (currentIndex < 0) throw new Error(`AppPath ${current} not found`);
  if (currentIndex >= pagesForRole.length - 1) return null;
  const nextIndex = currentIndex + 1;
  const nextPage = pagesForRole[nextIndex];
  return nextPage;
}

function getNext(
  urls: ContractUrls,
  pagesForRole: ContractFormRoute[],
  current: ContractPath
) {
  const nextPage = getNextPage(pagesForRole, current);
  return nextPage ? urls[nextPage.path] : null;
}

function getPrevPage(pagesForRole: ContractFormRoute[], current: ContractPath) {
  const currentIndex = pagesForRole.findIndex(
    (x) => x.path === current || x.also?.find((y) => y === current)
  );
  if (currentIndex < 0) throw new Error(`AppPath ${current} not found`);
  if (currentIndex < 1) return null;
  const prevIndex = currentIndex - 1;
  const prevPage = pagesForRole[prevIndex];
  return prevPage;
}

function getPrev(
  urls: ContractUrls,
  pagesForRole: ContractFormRoute[],
  current: ContractPath
) {
  const prevPage = getPrevPage(pagesForRole, current);
  return prevPage ? urls[prevPage.path] : null;
}

export function useNextUrl(current: ContractPath) {
  const urls = useContractUrls();
  const pagesForRole = usePagesForRole();
  return getNext(urls, pagesForRole, current);
}

export function usePrevUrl(current: ContractPath) {
  const urls = useContractUrls();
  const pagesForRole = usePagesForRole();
  return getPrev(urls, pagesForRole, current);
}

interface ContractFormRoute {
  path: ContractPath;
  also?: ContractPath[];
  only?: Role;
}

function useContractFormRoutes(): ContractFormRoute[] {
  const contract = useContract();
  const initiator = contract.initiator;

  const pages = useMemo(
    () => [
      {
        path: ContractPath.invitation,
        only: initiator,
      },
      {
        path: ContractPath.contract,
      },
      {
        path: ContractPath.type,
        also: [ContractPath.typePayment],
        only: initiator,
      },
      {
        path: ContractPath.financial,
        only: Role.Buyer,
      },
      {
        path: ContractPath.attachments,
        only: Role.Seller,
      },
      {
        path: ContractPath.trialMembership,
      },
      {
        path: ContractPath.insurance,
        only: Role.Buyer,
      },
      {
        path: ContractPath.ownershipTransfer,
      },
      {
        path: ContractPath.invite,
        only: initiator,
      },
      {
        path: ContractPath.preview,
      },
      {
        path: ContractPath.sign,
      },
    ],
    [initiator]
  );

  return pages;
}
